import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { TASKS_TIMEZONE } from '@/modules/common/helpers/taskDateTime.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { mapTabToEmptyStateText } from '@/modules/admin-dashboard/pages/Tasks.tsx';
import TaskHistoryItem from '@/modules/employee-dashboard/components/items/TaskHistoryItem.tsx';
import TaskItem from '@/modules/employee-dashboard/components/items/TaskItem.tsx';
import { useTasksListQuery } from '@/modules/employee-dashboard/queries/tasks.ts';
import { EmptyListState, PageLayout, SearchField, Spinner } from 'ui';
import {
  GetTaskListV2TasksGetParams,
  TaskListMode,
  TaskListWithRelationsSummaryResponse,
} from 'compliance-api';

const mapTabToListItem: Record<
  TaskListMode,
  FC<{ task: TaskListWithRelationsSummaryResponse; isUpcoming?: boolean }>
> = {
  [TaskListMode.Deleted]: TaskItem,
  [TaskListMode.AllTasks]: TaskItem,
  [TaskListMode.Todo]: TaskItem,
  [TaskListMode.Upcoming]: TaskItem,
  [TaskListMode.Overdue]: TaskItem,
  [TaskListMode.History]: TaskHistoryItem,
};

const Tasks = () => {
  const { category } = useParams();
  const businessId = useGetCurrentBusinessId();

  const mode =
    category && Object.values(TaskListMode).includes(category as TaskListMode)
      ? (category as TaskListMode)
      : TaskListMode.Todo;
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [businessId, mode]);

  const filters: GetTaskListV2TasksGetParams = useMemo(
    () => ({
      page: 1,
      page_size: 15,
      mode,
      filter_by_name: searchValue,
      timezone: TASKS_TIMEZONE,
    }),
    [mode, searchValue],
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage } =
    useTasksListQuery(filters);

  const tasks = useMemo(() => {
    return (data?.pages || []).flatMap(({ data }) => data);
  }, [data?.pages]);

  const TaskItem = mapTabToListItem[mode];
  return (
    <PageLayout
      title="Tasks"
      headerAction={
        <SearchField
          className="w-fit"
          placeholder="Search"
          value={searchValue}
          onChange={setSearchValue}
        />
      }
    >
      {(isLoading || (isFetching && !isFetchingNextPage)) && (
        <div className="flex items-center justify-center mt-[35%] ">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      <EmptyListState
        title={mapTabToEmptyStateText[mode].title}
        description={mapTabToEmptyStateText[mode].content}
        isLoading={isFetching}
        isSearchUsed={!!searchValue}
        itemsCount={tasks.length}
      />
      {!isLoading && !isFetching && !!tasks.length && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={data?.pages?.length || 0}
          hasMore={!!hasNextPage}
          className="w-full flex flex-col space-y-2"
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          {tasks.map(task => (
            <TaskItem key={task.id} task={task} isUpcoming={mode === TaskListMode.Upcoming} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Tasks, Role.COMPLIANCE_USER);
